import {
  Box,
  SimpleGrid,
  Skeleton,
  Flex,
  Pagination,
} from '@mantine/core'
import { IconUsersGroup } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { EmptyStateBox } from 'components/EmptyStateBox'
import { StudentCard } from './StudentCard'
import type { ManagedStudent } from 'api/arcMosaico/types'

const GRID_BREAKPOINTS = [
  { maxWidth: 'md' as const, cols: 2 },
  { maxWidth: 'sm' as const, cols: 1 },
]

interface Props {
  students: ManagedStudent[]
  loading: boolean
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  onEdit: (student: ManagedStudent) => void
  onResetPassword: (student: ManagedStudent) => void
}

export function StudentGrid({
  students,
  loading,
  page,
  totalPages,
  onPageChange,
  onEdit,
  onResetPassword,
}: Props) {
  const { t } = useTranslation()

  return (
    <>
      <Box mt='md'>
        {loading ? (
          <SimpleGrid
            cols={3}
            spacing='sm'
            breakpoints={GRID_BREAKPOINTS}
          >
            {Array.from({ length: 9 }, (_, i) => (
              <Skeleton
                key={i}
                height={160}
                radius='md'
                animate
              />
            ))}
          </SimpleGrid>
        ) : students.length === 0 ? (
          <EmptyStateBox
            withBorder
            icon={<IconUsersGroup size={48} stroke={1.5} color='gray' />}
            text={t('manage_students.empty')}
            textSize='md'
          />
        ) : (
          <SimpleGrid
            cols={3}
            spacing='sm'
            breakpoints={GRID_BREAKPOINTS}
          >
            {students.map(student => (
              <StudentCard
                key={student.id}
                student={student}
                onEdit={onEdit}
                onResetPassword={onResetPassword}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>

      {totalPages > 1 && (
        <Flex justify='center' mt='lg'>
          <Pagination
            value={page}
            onChange={onPageChange}
            total={totalPages}
            size='md'
          />
        </Flex>
      )}
    </>
  )
}
